import React, { useEffect, useState } from "react";
import Layout from "../components/Layout/Layout";
import BannerHeroSection from "../components/HeroSection/BannerHeroSection";
import BlogHeroSection from "../assets/BlogHeroSection.png";
import axios from "axios";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const Blog = () => {
  const [blogData, setBlogData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getBlogData = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/blogs`,
        );
        setBlogData(response.data.data || []);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching blog data:", error);
        setLoading(false);
      }
    };

    getBlogData();
  }, []);

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substr(0, maxLength) + "...";
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#B02526]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <BannerHeroSection
        title="Blog"
        subtitle="Stay updated with the latest insights, tips, and news from our training experts"
        bannerImage={BlogHeroSection}
        height="h-64 md:h-80 lg:h-96 xl:h-[500px]"
        className="-mx-4 md:-mx-12 lg:-mx-16 mb-6 md:mb-8 lg:mb-10"
      />

      <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-7xl mx-auto">
            {/* Blog Grid */}
            {blogData.length > 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10"
              >
                {blogData.map((blog, index) => (
                  <motion.article
                    key={blog._id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 * index }}
                    className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
                  >
                    {/* Blog Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={`${process.env.REACT_APP_BASE_URL}/${blog.image}`}
                        alt={blog.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    </div>

                    {/* Blog Content */}
                    <div className="p-6">
                      {/* Date */}
                      <div className="flex items-center gap-2 mb-3">
                        <svg
                          className="w-4 h-4 text-[#B02526]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span className="text-sm text-[#5E6282] font-medium">
                          {formatDate(blog.date || blog.createdAt)}
                        </span>
                      </div>

                      {/* Title */}
                      <h2 className="text-xl lg:text-2xl font-bold text-[#14183E] mb-3 leading-tight">
                        {blog.title}
                      </h2>

                      {/* Excerpt */}
                      <p className="text-[#5E6282] text-sm lg:text-base leading-relaxed mb-4">
                        {truncateText(blog.desc || blog.description, 120)}
                      </p>

                      {/* Read More Link */}
                      <Link
                        to={`/blog-inside/${blog._id}`}
                        className="inline-flex items-center gap-2 text-[#B02526] font-semibold hover:text-[#8B1E1F] transition-colors duration-300"
                      >
                        Read More
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </Link>
                    </div>
                  </motion.article>
                ))}
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center py-16"
              >
                <div className="text-6xl mb-4">📝</div>
                <h3 className="text-2xl font-bold text-[#14183E] mb-2">
                  No Articles Yet
                </h3>
                <p className="text-[#5E6282]">
                  Check back soon for our latest blog posts and insights.
                </p>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Blog;
